<template>
  <div class="purchase-list">
    <div class="page-header">
      <h2>原料采购</h2>
      <a-space>
        <a-button @click="handleExport" :loading="exportLoading">
          <template #icon><DownloadOutlined /></template>
          导出Excel
        </a-button>
        <a-button type="primary" @click="openEdit(null)">
          <template #icon><PlusOutlined /></template>
          新增采购
        </a-button>
      </a-space>
    </div>

    <a-card class="filter-card">
      <a-form layout="inline">
        <a-form-item label="原料">
          <a-select
            v-model:value="searchForm.materialId"
            placeholder="请选择原料"
            allow-clear
            show-search
            :filter-option="filterOption"
            style="width: 220px"
          >
            <a-select-option v-for="m in materials" :key="m.id" :value="m.id" :label="m.name">
              {{ m.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="类型">
          <a-select v-model:value="searchForm.type" placeholder="全部" allow-clear style="width: 120px">
            <a-select-option value="FABRIC">面料</a-select-option>
            <a-select-option value="ACCESSORY">辅料</a-select-option>
            <a-select-option value="CONSUMABLE">耗材</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="供应商">
          <a-auto-complete
            v-model:value="searchForm.supplier"
            :options="supplierOptions"
            :filter-option="supplierFilter"
            placeholder="供应商"
            allow-clear
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="日期">
          <a-range-picker v-model:value="searchForm.dateRange" format="YYYY-MM-DD" style="width: 240px" />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="handleSearch">查询</a-button>
            <a-button @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card>
      <a-table
        :columns="columns"
        :data-source="records"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
        :scroll="{ x: 1100 }"
        :sticky="{ offsetScroll: 0, getContainer: getScrollContainer }"
        size="middle"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'purchaseDate'">{{ formatDate(record.purchaseDate) }}</template>
          <template v-if="column.key === 'type'">
            <a-tag :color="typeColor(record.type)">{{ typeLabel(record.type) }}</a-tag>
          </template>
          <template v-if="column.key === 'quantity'">
            {{ formatNum(record.quantity) }} {{ unitText(record.unit) }}
          </template>
          <template v-if="column.key === 'unitPrice'">{{ formatMoney(record.unitPrice) }}</template>
          <template v-if="column.key === 'shippingFee'">{{ formatMoney(record.shippingFee) }}</template>
          <template v-if="column.key === 'totalAmount'">
            <strong>{{ formatMoney(record.totalAmount) }}</strong>
          </template>
          <template v-if="column.key === 'action'">
            <a-space :size="4">
              <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
              <a-popconfirm title="确定删除该采购吗？" @confirm="handleDelete(record)">
                <a-button type="link" size="small" danger>删除</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="editVisible"
      :title="form.id ? '编辑采购' : '新增采购'"
      @ok="handleSubmit"
      :confirm-loading="submitting"
      width="640px"
    >
      <a-form layout="vertical">
        <a-row :gutter="16">
          <a-col :span="14">
            <a-form-item label="供应商">
              <a-auto-complete
                v-model:value="form.supplier"
                :options="filteredSupplierOptions"
                :filter-option="false"
                placeholder="选择或输入供应商（已选时，下方原料按该供应商排序）"
                style="width: 100%"
                allow-clear
              />
              <div style="color:#999;font-size:12px;margin-top:4px">
                输入新供应商时，保存后会自动添加到该原料的供应商列表
              </div>
            </a-form-item>
          </a-col>
          <a-col :span="10">
            <a-form-item label="采购日期">
              <a-date-picker v-model:value="form.purchaseDate" format="YYYY-MM-DD" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="14">
            <a-form-item label="原料" required>
              <a-radio-group v-model:value="formTypeFilter" size="small" button-style="solid" style="margin-bottom: 6px">
                <a-radio-button value="">全部</a-radio-button>
                <a-radio-button value="FABRIC">面料</a-radio-button>
                <a-radio-button value="ACCESSORY">辅料</a-radio-button>
                <a-radio-button value="CONSUMABLE">耗材</a-radio-button>
              </a-radio-group>
              <a-select
                v-model:value="form.materialId"
                placeholder="请选择原料（可搜索）"
                show-search
                :filter-option="filterOption"
                @change="onMaterialChange"
              >
                <a-select-option v-for="m in filteredFormMaterials" :key="m.id" :value="m.id" :label="m.name">
                  <span v-if="matchesSupplier(m)" style="color:#1890ff;font-weight:500;margin-right:4px">●</span>
                  {{ m.name }} <span style="color:#999">({{ typeLabel(m.type) }} / {{ unitText(m.unit) }})</span>
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="10">
            <a-form-item :label="`数量（${unit}）`" required>
              <a-input-number v-model:value="form.quantity" :min="0" :precision="2" placeholder="0.00" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="单价">
              <a-input-number v-model:value="form.unitPrice" :min="0" :precision="2" placeholder="0.00" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="运费">
              <a-input-number v-model:value="form.shippingFee" :min="0" :precision="2" placeholder="0.00" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="总价">
              <a-input-number
                v-model:value="form.totalAmount"
                :min="0"
                :precision="2"
                placeholder="自动计算"
                style="width: 100%"
                addon-before="¥"
              />
              <div v-if="totalDiffers" class="total-hint" @click="resetTotal">
                自动计算 ¥{{ formatMoney(computedTotal) }} (点击重置)
              </div>
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-form-item label="备注">
              <a-textarea v-model:value="form.remark" :rows="2" :maxlength="500" show-count />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, computed, watch, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons-vue'
import dayjs from 'dayjs'
import { materialApi } from '@/api'

export default {
  name: 'PurchaseList',
  components: { PlusOutlined, DownloadOutlined },
  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const exportLoading = ref(false)
    const records = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const materials = ref([])
    const supplierOptions = ref([])
    const supplierFilter = (input, option) => (option.value || '').includes(input)

    const searchForm = reactive({
      materialId: undefined,
      type: undefined,
      supplier: '',
      dateRange: []
    })

    const pagination = reactive({
      current: 1, pageSize: 20, total: 0,
      showSizeChanger: true,
      showTotal: (total) => `共 ${total} 条`
    })

    const editVisible = ref(false)
    const form = reactive({
      id: null, materialId: undefined,
      purchaseDate: null, supplier: '',
      quantity: 0, unitPrice: 0, shippingFee: 0,
      totalAmount: 0, remark: ''
    })
    /** When true, user hasn't manually overridden total, auto-sync from computed */
    const totalAutoSync = ref(true)

    const columns = [
      { title: '采购日期', key: 'purchaseDate', width: 110, fixed: 'left' },
      { title: '类型', key: 'type', width: 70 },
      { title: '原料', dataIndex: 'materialName', key: 'materialName', width: 160, ellipsis: true },
      { title: '供应商', dataIndex: 'supplier', key: 'supplier', width: 100, ellipsis: true },
      { title: '数量', key: 'quantity', width: 120, align: 'right' },
      { title: '单价', key: 'unitPrice', width: 80, align: 'right' },
      { title: '运费', key: 'shippingFee', width: 70, align: 'right' },
      { title: '总价', key: 'totalAmount', width: 100, align: 'right' },
      { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true },
      { title: '操作', key: 'action', width: 130, fixed: 'right' }
    ]

    const typeLabel = (t) => ({ FABRIC: '面料', ACCESSORY: '辅料', CONSUMABLE: '耗材' })[t] || t
    const typeColor = (t) => ({ FABRIC: 'cyan', ACCESSORY: 'purple', CONSUMABLE: 'orange' })[t] || 'default'

    const filterOption = (input, option) => (option.label || '').toLowerCase().includes(input.toLowerCase())
    const getScrollContainer = () => document.querySelector('.content') || window

    const currentMaterial = computed(() => materials.value.find(m => m.id === form.materialId))
    const unit = computed(() => unitText(currentMaterial.value?.unit) || '')

    const formTypeFilter = ref('')

    const matchesSupplier = (m) => {
      const s = (form.supplier || '').trim()
      if (!s || !m || !m.suppliers) return false
      return m.suppliers.split(',').map(x => x.trim()).filter(Boolean).includes(s)
    }

    const filteredFormMaterials = computed(() => {
      const base = formTypeFilter.value
        ? materials.value.filter(m => m.type === formTypeFilter.value)
        : materials.value.slice()
      const s = (form.supplier || '').trim()
      if (!s) return base
      // stable sort: matching supplier first
      const matched = []
      const rest = []
      base.forEach(m => (matchesSupplier(m) ? matched : rest).push(m))
      return [...matched, ...rest]
    })

    /** Supplier options for the form: material's own suppliers first, then global history */
    const formSupplierOptions = computed(() => {
      const matSuppliers = currentMaterial.value && currentMaterial.value.suppliers
        ? currentMaterial.value.suppliers.split(',').filter(Boolean)
        : []
      const matSet = new Set(matSuppliers)
      const globalRest = supplierOptions.value
        .map(o => o.value)
        .filter(v => v && !matSet.has(v))
      const result = matSuppliers.map(v => ({ value: v, label: `${v}（本原料）` }))
      globalRest.forEach(v => result.push({ value: v }))
      return result
    })

    /**
     * 下拉实际展示的选项（自带过滤，配合 :filter-option="false" 用）：
     *  - 输入为空 → 显示全部
     *  - 当前值正好等于某个已有供应商（已选中）→ 也显示全部，方便切换、不被锁死
     *  - 正在输入（部分关键词）→ 按关键词包含过滤
     *  - 输入全新供应商（无匹配）→ 无建议，但可自由输入提交
     */
    const filteredSupplierOptions = computed(() => {
      const all = formSupplierOptions.value
      const kw = (form.supplier || '').trim()
      if (!kw) return all
      if (all.some(o => o.value === kw)) return all
      return all.filter(o => (o.value || '').includes(kw))
    })

    const computedTotal = computed(() => (Number(form.quantity) || 0) * (Number(form.unitPrice) || 0) + (Number(form.shippingFee) || 0))
    const totalDiffers = computed(() => Math.abs((Number(form.totalAmount) || 0) - computedTotal.value) > 0.005)

    // Auto-sync total when components change, unless user has overridden
    watch([() => form.quantity, () => form.unitPrice, () => form.shippingFee], () => {
      if (totalAutoSync.value) {
        form.totalAmount = computedTotal.value
      }
    })
    // Detect user manual edits to totalAmount
    watch(() => form.totalAmount, (v) => {
      if (Math.abs((Number(v) || 0) - computedTotal.value) > 0.005) {
        totalAutoSync.value = false
      }
    })

    const resetTotal = () => {
      form.totalAmount = computedTotal.value
      totalAutoSync.value = true
    }

    const formatDate = (s) => !s || s.length !== 8 ? s || '' : `${s.substring(0,4)}-${s.substring(4,6)}-${s.substring(6,8)}`
    const formatNum = (n) => Number(n || 0).toFixed(2).replace(/\.00$/, '')
    const formatMoney = (n) => Number(n || 0).toFixed(2)
    const unitText = (u) => {
      if (!u) return ''
      const legacy = { METER: '米', YARD: '码', PIECE: '个', PAIR: '对', PACK: '包' }
      return legacy[u] || u
    }

    const buildParams = () => {
      const params = {
        materialId: searchForm.materialId || undefined,
        type: searchForm.type || undefined,
        supplier: searchForm.supplier || undefined,
        pageNo: pagination.current,
        pageSize: pagination.pageSize
      }
      if (searchForm.dateRange?.length === 2) {
        params.startDate = searchForm.dateRange[0].format('YYYYMMDD')
        params.endDate = searchForm.dateRange[1].format('YYYYMMDD')
      }
      return params
    }

    const loadList = async () => {
      loading.value = true
      try {
        const result = await materialApi.listPurchase(buildParams())
        if (result.code === '0') {
          records.value = result.data.records || []
          pagination.total = result.data.total || 0
        }
      } finally {
        loading.value = false
      }
    }

    const loadMaterials = async () => {
      try {
        const result = await materialApi.optionsMaterial({ status: 1 })
        if (result.code === '0') materials.value = result.data || []
      } catch (e) { console.error(e) }
    }

    const loadSuppliers = async () => {
      try {
        const result = await materialApi.listSuppliers()
        if (result.code === '0') {
          supplierOptions.value = (result.data || []).map(s => ({ value: s }))
        }
      } catch (e) { console.error(e) }
    }

    const handleTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      loadList()
    }
    const handleSearch = () => { pagination.current = 1; loadList() }
    const handleReset = () => {
      Object.assign(searchForm, { materialId: undefined, type: undefined, supplier: '', dateRange: [] })
      pagination.current = 1
      loadList()
    }
    const onMaterialChange = () => { /* derived from currentMaterial */ }

    const openEdit = (record) => {
      formTypeFilter.value = ''
      if (record) {
        form.id = record.id
        form.materialId = record.materialId
        form.purchaseDate = record.purchaseDate ? dayjs(record.purchaseDate, 'YYYYMMDD') : null
        form.supplier = record.supplier || ''
        form.quantity = Number(record.quantity || 0)
        form.unitPrice = Number(record.unitPrice || 0)
        form.shippingFee = Number(record.shippingFee || 0)
        form.totalAmount = Number(record.totalAmount || 0)
        form.remark = record.remark || ''
        // If stored total matches computed, keep auto-sync; else treat as manual override
        const computed = form.quantity * form.unitPrice + form.shippingFee
        totalAutoSync.value = Math.abs(form.totalAmount - computed) <= 0.005
      } else {
        Object.assign(form, {
          id: null, materialId: undefined, purchaseDate: dayjs(),
          supplier: '', quantity: 0, unitPrice: 0, shippingFee: 0,
          totalAmount: 0, remark: ''
        })
        totalAutoSync.value = true
      }
      editVisible.value = true
    }

    const handleSubmit = async () => {
      if (!form.materialId) { message.error('请选择原料'); return }
      if (!form.quantity || form.quantity <= 0) { message.error('数量必须大于 0'); return }
      submitting.value = true
      try {
        const result = await materialApi.savePurchase({
          id: form.id, materialId: form.materialId,
          purchaseDate: form.purchaseDate ? form.purchaseDate.format('YYYYMMDD') : null,
          supplier: form.supplier,
          quantity: form.quantity, unitPrice: form.unitPrice || 0, shippingFee: form.shippingFee || 0,
          totalAmount: form.totalAmount || 0,
          remark: form.remark
        })
        if (result.code === '0') {
          message.success('保存成功')
          editVisible.value = false
          loadList()
          loadSuppliers()
          loadMaterials()
        } else {
          message.error(result.msg || '保存失败')
        }
      } finally {
        submitting.value = false
      }
    }

    const handleDelete = async (record) => {
      try {
        const result = await materialApi.deletePurchase(record.id)
        if (result.code === '0') { message.success('删除成功'); loadList() }
        else message.error(result.msg || '删除失败')
      } catch (e) { console.error(e) }
    }

    const handleExport = async () => {
      exportLoading.value = true
      try {
        const params = buildParams()
        delete params.pageNo; delete params.pageSize
        const response = await materialApi.exportPurchase(params)
        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const now = new Date()
        const ts = now.getFullYear() + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0') +
          '_' + String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0') + String(now.getSeconds()).padStart(2, '0')
        link.download = `原料采购_${ts}.xlsx`
        document.body.appendChild(link); link.click(); document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        message.success('导出成功')
      } catch (e) {
        console.error(e); message.error('导出失败')
      } finally {
        exportLoading.value = false
      }
    }

    onMounted(() => { loadMaterials(); loadSuppliers(); loadList() })

    return {
      loading, submitting, exportLoading,
      records, materials, supplierOptions, supplierFilter,
      searchForm, pagination, columns,
      editVisible, form, unit,
      formTypeFilter, filteredFormMaterials, formSupplierOptions, filteredSupplierOptions, matchesSupplier,
      computedTotal, totalDiffers, resetTotal,
      filterOption, getScrollContainer,
      formatDate, formatNum, formatMoney, unitText,
      typeLabel, typeColor,
      handleTableChange, handleSearch, handleReset, onMaterialChange,
      openEdit, handleSubmit, handleDelete, handleExport
    }
  }
}
</script>

<style scoped>
.purchase-list { padding: 0; }
.page-header {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;
}
.page-header h2 { margin: 0; }
.filter-card { margin-bottom: 16px; }

.total-hint {
  font-size: 11px;
  color: #fa8c16;
  margin-top: 4px;
  cursor: pointer;
  user-select: none;
}
.total-hint:hover { color: #1890ff; text-decoration: underline; }
</style>
